import React from "react";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";
import { Calendar, MapPin, PartyPopper, Sparkles, Clock } from "lucide-react";

const LaunchParty = () => {
  const { LAUNCH_PARTY, EVENT } = useCruise();
  const lp = LAUNCH_PARTY;
  return (
    <section id="launch" className="relative py-24 overflow-hidden bg-[#0A0A0A]">
      {/* background image + overlays */}
      <div className="absolute inset-0">
        <img src={lp.image} alt="Launch party" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/92 to-[#8c080e]/60" />
        <div className="absolute inset-0 bg-[#0A0A0A]/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: heading & meta */}
          <div>
            <span className="inline-flex items-center gap-2 bg-[var(--hh-gold)] text-[#0A0A0A] font-cond uppercase text-xs font-bold tracking-widest rounded-full px-3 py-1.5 mb-5">
              <Sparkles size={14} /> Pre-Cruise Kickoff
            </span>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.92]">
              The <span className="text-[#E50914]">Launch</span> Party
            </h2>
            <div className="flex items-center gap-3 mt-4">
              <span className="section-line" />
              <p className="font-cond uppercase tracking-[0.25em] text-[var(--hh-gold)] text-sm">{lp.tagline}</p>
            </div>

            <p className="mt-6 text-white/75 text-lg max-w-xl">{lp.intro}</p>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="text-[#E50914]" size={22} />
                <div>
                  <p className="font-cond uppercase text-xs text-white/50 tracking-widest">When</p>
                  <p className="font-cond font-semibold text-white text-base">{lp.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="text-[#E50914]" size={22} />
                <div>
                  <p className="font-cond uppercase text-xs text-white/50 tracking-widest">Where</p>
                  <p className="font-cond font-semibold text-white text-base">
                    {lp.venue} &bull; {lp.location}
                  </p>
                </div>
              </div>
            </div>

            <a href={EVENT.bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-9">
              <Button className="group bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest text-base rounded-full px-8 py-6 h-auto">
                <PartyPopper size={18} className="mr-2" /> Reserve Your Spot
              </Button>
            </a>
          </div>

          {/* Right: schedule cards */}
          <div className="space-y-5">
            {lp.events.map((e, i) => (
              <div
                key={e.title}
                className="group relative bg-[#111111]/90 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-[#E50914] p-6 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="absolute -top-3 -left-3 flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914] text-white font-display text-lg ring-4 ring-[#0A0A0A]">
                  {i + 1}
                </span>
                <div className="flex items-center gap-2 text-[var(--hh-gold)] mb-2 pl-6">
                  <Clock size={15} />
                  <span className="font-cond uppercase tracking-widest text-sm font-semibold">{e.time}</span>
                </div>
                <h3 className="font-cond font-bold uppercase text-white text-xl tracking-wide pl-6">{e.title}</h3>
                <p className="text-white/60 text-sm mt-2 pl-6">{e.desc}</p>
                {e.map && (
                  <a href={e.map.url} target="_blank" rel="noopener noreferrer" data-testid={`launch-map-link-${i}`} className="inline-flex items-center gap-1.5 mt-3 ml-6 font-cond uppercase tracking-widest text-xs font-semibold text-[var(--hh-gold)] hover:text-white transition-colors">
                    <MapPin size={14} /> {e.map.label}
                  </a>
                )}
              </div>
            ))}

            <div className="flex items-start gap-3 bg-[#1B8A5A]/15 border border-[#1B8A5A]/40 p-4">
              <Sparkles size={16} className="text-[#1B8A5A] mt-0.5 shrink-0" />
              <p className="text-white/70 text-xs">{lp.note}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LaunchParty;
